import React from "react";
import { Compass, Lightbulb, GraduationCap, Activity, ArrowUpRight } from "lucide-react";

const DIVISIONS = [
  {
    id: "strategy",
    number: "01",
    icon: Compass,
    title: "MAGIS Strategy",
    description:
      "We help companies define who they want to become — and identify the short-, medium- and long-term actions needed to get there.",
    keywords: ["Vision setting", "5-year planning", "Leadership alignment"],
  },
  {
    id: "innovation",
    number: "02",
    icon: Lightbulb,
    title: "MAGIS Innovation & Change",
    description:
      "We translate strategic intent into new products, services, go-to-market models and ways of working that organisations actually adopt.",
    keywords: ["Pretotyping", "New ventures", "Operating models"],
  },
  {
    id: "academy",
    number: "03",
    icon: GraduationCap,
    title: "MAGIS Academy",
    description:
      "We teach people inside organisations to drive innovation themselves — through lean experimentation and learning by failing forward.",
    keywords: ["Design Thinking", "Lean testing", "MVP coaching"],
  },
  {
    id: "pulse",
    number: "04",
    icon: Activity,
    title: "MAGIS Pulse",
    description:
      "We run the day-to-day operational activities that keep strategy alive — efficient, accountable and locked onto the priorities that matter.",
    keywords: ["Operations", "Project office", "Continuous alignment"],
  },
];

export const Divisions = () => {
  return (
    <section
      id="divisions"
      data-testid="divisions-section"
      className="relative bg-brand-teal text-white py-24 md:py-40 overflow-hidden grain"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#154247] blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#2EC4B6]/20 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-3">
            <span className="overline text-[#2EC4B6]">— Our divisions</span>
          </div>
          <div className="md:col-span-9">
            <h2
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05]"
              data-testid="divisions-heading"
            >
              Four practices.<br />
              <span className="text-white/50">One shared</span> obsession with impact.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {DIVISIONS.map((d) => {
            const Icon = d.icon;
            return (
              <article
                key={d.id}
                data-testid={`division-card-${d.id}`}
                className="group relative bg-[#0A2427] p-8 md:p-12 transition-all duration-500 hover:bg-[#154247] cursor-default"
              >
                <div className="flex items-start justify-between mb-10">
                  <div className="overline text-[#2EC4B6]">{d.number}</div>
                  <Icon
                    size={36}
                    strokeWidth={1.25}
                    className="text-[#2EC4B6] group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-display text-3xl md:text-4xl mb-5 tracking-tight">
                  {d.title}
                </h3>
                <p className="text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-md">
                  {d.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {d.keywords.map((k) => (
                    <span
                      key={k}
                      className="text-xs px-3 py-1.5 border border-white/15 text-white/60"
                    >
                      {k}
                    </span>
                  ))}
                </div>
                <ArrowUpRight
                  size={20}
                  className="absolute bottom-8 right-8 text-white/30 group-hover:text-[#2EC4B6] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Divisions;
